// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
  BrandData, CycleData, Meta, RebrandData, RegionSeries, SeriesIndex, Site, SpreadData, Suburb,
} from './types';

const cache = new Map<string, Promise<unknown>>();
const controllers = new Set<AbortController>();

/**
 * Fetch and cache a JSON file. Failures reject rather than resolving to a
 * sentinel, so every caller is forced to render a real error state.
 */
export function loadJson<T>(name: string): Promise<T> {
  const hit = cache.get(name);
  if (hit) return hit as Promise<T>;
  const controller = new AbortController();
  controllers.add(controller);
  const p = fetch(`data/${name}`, { signal: controller.signal })
    .then((r) => {
      if (!r.ok) throw new Error(`Could not load ${name} (HTTP ${r.status})`);
      return r.json() as Promise<T>;
    })
    .finally(() => controllers.delete(controller));
  p.catch(() => cache.delete(name));
  cache.set(name, p);
  return p;
}

/** Abort every in-flight request — used when the view changes. */
export function abortPending(): void {
  for (const c of controllers) c.abort();
  controllers.clear();
}

export const loadMeta = () => loadJson<Meta>('meta.json');
export const loadCycle = () => loadJson<CycleData>('cycle.json');
export const loadSeriesIndex = () => loadJson<SeriesIndex>('series-index.json');
export const loadRegionSeries = (slug: string) => loadJson<RegionSeries>(`series/${slug}.json`);
export const loadSites = () => loadJson<{ sites: Site[] }>('sites.json');
export const loadSuburbs = () => loadJson<{ suburbs: Suburb[] }>('suburbs.json');
export const loadBrands = () => loadJson<BrandData>('brands.json');
export const loadRebrands = () => loadJson<RebrandData>('rebrands.json');
export const loadSpread = () => loadJson<SpreadData>('spread.json');
export const loadBoundaries = () => loadJson<GeoJSON.FeatureCollection>('sa4.geojson');

/** Persisted UI preferences. Never throws — private mode blocks localStorage. */
export const prefs = {
  get(key: string, fallback: string): string {
    try {
      return window.localStorage.getItem(`fuel:${key}`) ?? fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(`fuel:${key}`, value);
    } catch {
      /* storage unavailable — preferences simply do not persist */
    }
  },
};
